import { useEffect, useState } from "react";
import { Button, Modal, Form } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { Plus, PencilSquare, Trash } from "react-bootstrap-icons";
import Swal from 'sweetalert2';
import api from "../api/axios";


export default function HolidayPage() {
    const [search, setSearch] = useState("");
    const [filteredHolidays, setFilteredHolidays] = useState([]);
    const [holidays, setHolidays] = useState([]);
    const [loadingTable, setLoadingTable] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [errors, setErrors] = useState({});
    const [loadingSave, setLoadingSave] = useState(false);
  
    const [formData, setFormData] = useState({
        id: null,
        fecha: '',
        nombre: ""
    });

    const normalize = (txt) =>
        String(txt ?? "")
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "");

    
    // ==============================
    // Fetch inicial
    // ==============================
    useEffect(() => {
        fetchHolidays();
    }, []);

    const fetchHolidays = async () => {
        try {
            setLoadingTable(true);
            const res = await api.get("/holidays");
            setFilteredHolidays(res.data.data);
            setHolidays(res.data.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoadingTable(false);
        }
    }

    useEffect(() => {
        const result = holidays.filter(
        (h) =>
            normalize(h.nombre)?.includes(normalize(search)) ||
            normalize(h.anio)?.includes(normalize(search)) ||
            normalize(h.fecha)?.includes(normalize(search))
        );
        setFilteredHolidays(result);
    }, [search, holidays]);

    const handleClose = () => {
        setShowModal(false);
        setShowDeleteModal(false);
        setIsEditing(false);
    };

    const handleShowEdit = (holiday) => {
        setIsEditing(true);
        setErrors({});
        setFormData({
            id: holiday.id,
            fecha: holiday.fecha,
            nombre: holiday.nombre
        });

        setShowModal(true);
    }

    const handleShowDelete = (holiday) => {
        setErrors({});
        setFormData({
            id: holiday.id,
            fecha: holiday.fecha,
            nombre: holiday.nombre
        });

        setShowDeleteModal(true);
    }

    const handleShowNew = () => {
        setIsEditing(false);
        setErrors({});
        setFormData({
            id: null,
            fecha: '',
            nombre: ""
        });
        setShowModal(true);
    }
    
    // ==============================
    // Guardar
    // ==============================
    const handleSave = async () => {
        setLoadingSave(true);
        setErrors({});

        const payload = {
            ...formData
        };
        
        try {
            if (isEditing) {
                await api.put(`/holidays/${formData.id}`, payload);
            } else {
                await api.post("/holidays", payload);
            }
            handleClose();
            fetchHolidays();
        } catch (err) {
            if (err.response?.status === 422) {
                setErrors(err.response.data.errors);
            } else {
            Swal.fire({
                title: 'Error',
                text: "Error desconocido al guardar",
                icon: 'error',
                timer: 1500,
                showConfirmButton: false
            });
            }
        } finally {
            setLoadingSave(false);
        }
    };

    // ==============================
    // Eliminar
    // ==============================
    const handleDelete = async () => {
        setLoadingSave(true);
        setErrors({});

        const payload = {
            ...formData
        };
        try {
            await api.delete(`/holidays/${formData.id}`, payload);
            
            handleClose();
            fetchHolidays();
        } catch (err) {
            if (err.response?.status === 422) {
            setErrors(err.response.data.errors);
            } else {
            Swal.fire({
                title: 'Error',
                text: "Error desconocido al guardar",
                icon: 'error',
                timer: 1500,
                showConfirmButton: false
            });
            }
        } finally {
            setLoadingSave(false);
        }
    };

    // ==============================
    // Columnas tabla
    // ==============================
    const columns = [
        { name: "Fecha", selector: (row) => row.fecha, sortable: true },
        { name: "Nombre", selector: (row) => row.nombre, sortable: true },
        { name: "Año", selector: (row) => row.anio, sortable: true },
        {
        name: "Opciones",
        cell: (row) => (
            <div className="d-flex gap-2">
                <Button
                    variant="outline-info"
                    size="sm"
                    onClick={() => handleShowEdit(row)}
                >
                    <PencilSquare />
                </Button>
                <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleShowDelete(row)}
                >
                    <Trash />
                </Button>
            </div>
        ),
        ignoreRowClick: true,
        },
    ];

    return (
        <div className="mt-4">
            {/* TABLA */}
            <DataTable
                title="Días Festivos"
                columns={columns}
                data={filteredHolidays}
                pagination
                highlightOnHover
                striped
                progressPending={loadingTable}
                progressComponent={
                    <div className="text-center p-3">
                        <div className="spinner-border text-primary"></div>
                        <p>Cargando...</p>
                    </div>
                }
                subHeader
                subHeaderComponent={
                <div className="d-flex justify-content-between w-100">
                    <Button variant="outline-success" size="sm" onClick={handleShowNew}>
                    Nuevo <Plus />
                    </Button>

                    <input
                    type="text"
                    className="form-control w-25"
                    placeholder="Buscar..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                }
            />

            {/* MODAL */}
            <Modal show={showModal} onHide={handleClose} size="md">
                <Modal.Header closeButton>
                    <Modal.Title>{isEditing ? "Editar Día Festivo" : "Nuevo Día Festivo"}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <Form>
                    {/* Nombre */}
                    <Form.Group className="mb-3">
                        <Form.Label>Nombre</Form.Label>
                        <Form.Control
                        type="text"
                        value={formData.nombre}
                        onChange={(e) =>
                            setFormData({ ...formData, nombre: e.target.value })
                        }
                        />
                        {errors.nombre?.map((e, i) => (
                        <Form.Text key={i} className="text-danger d-block">
                            {e}
                        </Form.Text>
                        ))}
                    </Form.Group>

                    {/* Año */}
                    {/* <Form.Group className="mb-3">
                        <Form.Label>Año</Form.Label>
                        <Form.Control
                        type="number"
                        value={formData.anio}
                        onChange={(e) =>
                            setFormData({ ...formData, anio: e.target.value })
                        }
                        />
                        {errors.year?.map((e, i) => (
                        <Form.Text key={i} className="text-danger d-block">
                            {e}
                        </Form.Text>
                        ))}
                    </Form.Group> */}

                    {/* Fecha */}
                    <Form.Group className="mb-3">
                        <Form.Label>Fecha</Form.Label>
                        <Form.Control
                        type="date"
                        min={new Date().toISOString().split('T')[0]}
                        value={formData.fecha}
                        onChange={(e) =>
                            setFormData({ ...formData, fecha: e.target.value })
                        }
                        />
                        {errors.fecha?.map((e, i) => (
                        <Form.Text key={i} className="text-danger d-block">
                            {e}
                        </Form.Text>
                        ))}
                    </Form.Group>
                    </Form>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        Cancelar
                    </Button>
                    <Button variant="primary" onClick={handleSave} disabled={loadingSave}>
                    {loadingSave ? (
                        <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Guardando...
                        </>
                    ) : isEditing ? (
                        "Actualizar"
                    ) : (
                        "Guardar"
                    )}
                    </Button>
                </Modal.Footer>
            </Modal>
            
            {/* MODAL DELETE */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Eliminación de día Festivo</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-muted">
                        Al borrar el día festivo no se podrá recuperar ¿Desea continuar?.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Cancelar
                    </Button>
                    <Button variant="danger" onClick={handleDelete} disabled={loadingSave}>
                    {loadingSave ? (
                        <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Cargando...
                        </>
                    ) :  (
                        "Eliminar"
                    )}
                    </Button>
                </Modal.Footer>
            </Modal>

        </div>
    );
}